import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ClipboardCheck,
  User,
  MapPin,
  Phone,
  Calendar,
  Wrench,
  Save,
  Download,
  Pen,
  Check,
  Lock,
  Play,
  Clock,
  ArrowLeft,
  X,
  Package,
  UserCog,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format date to yyyy-mm-dd hh:mm:ss AM/PM
const formatDateTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12; // 0 should be 12
  const hoursStr = String(hours).padStart(2, "0");
  return `${year}-${month}-${day} ${hoursStr}:${minutes}:${seconds} ${ampm}`;
};

// Signature Canvas Component
const SignatureCanvas = ({ onSave, disabled, existingSignature }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#1e293b";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    if (disabled) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || disabled) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const saveSignature = () => {
    if (!hasSignature) {
      toast.error("Please draw your signature first");
      return;
    }
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onSave(dataUrl);
  };

  if (existingSignature) {
    return (
      <div className="border rounded-sm p-2 bg-slate-50">
        <img src={existingSignature} alt="Signature" className="max-h-20" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={400}
        height={150}
        className={`border-2 rounded-sm w-full touch-none ${
          disabled ? "bg-slate-100 cursor-not-allowed" : "bg-white cursor-crosshair border-dashed border-slate-300"
        }`}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      {!disabled && (
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={clearSignature}>
            Clear
          </Button>
          <Button type="button" size="sm" onClick={saveSignature}>
            <Check className="h-4 w-4 mr-1" />
            Confirm Signature
          </Button>
        </div>
      )}
    </div>
  );
};

const JobCard = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [jobCard, setJobCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showWorkerSignDialog, setShowWorkerSignDialog] = useState(false);
  const [showCustomerSignDialog, setShowCustomerSignDialog] = useState(false);
  const [workerSignName, setWorkerSignName] = useState("");
  const [customerSignName, setCustomerSignName] = useState("");
  const [workerSignature, setWorkerSignature] = useState(null);
  const [customerSignature, setCustomerSignature] = useState(null);
  const [workers, setWorkers] = useState([]);

  const fetchJobCard = useCallback(async () => {
    try {
      const [jobCardRes, userRes] = await Promise.all([
        axios.get(`${API}/job-cards/${bookingId}`, { withCredentials: true }),
        axios.get(`${API}/auth/me`, { withCredentials: true }),
      ]);
      setJobCard(jobCardRes.data);
      setCurrentUser(userRes.data);
      setWorkerSignName(userRes.data?.name || "");
      
      // Fetch workers list for admin/manager to assign
      if (userRes.data?.role === "admin" || userRes.data?.role === "manager") {
        try {
          const workersRes = await axios.get(`${API}/users`, { withCredentials: true });
          const availableWorkers = workersRes.data.filter(
            (u) => u.is_active && (u.role === "worker" || u.role === "manager" || u.role === "admin")
          );
          setWorkers(availableWorkers);
        } catch (err) {
          console.log("Could not fetch workers list");
        }
      }
    } catch (error) {
      console.error("Error fetching job card:", error);
      if (error.response?.status === 404) {
        toast.error("Job card not found");
      } else {
        toast.error("Failed to load job card");
      }
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    fetchJobCard();
  }, [fetchJobCard]);

  const handleChecklistChange = (field, value) => {
    if (jobCard?.is_locked) return;
    setJobCard((prev) => ({
      ...prev,
      sanitation_checklist: {
        ...prev.sanitation_checklist,
        [field]: value,
      },
    }));
  };

  const handleTapChange = (index, field, value) => {
    if (jobCard?.is_locked) return;
    setJobCard((prev) => {
      const newTaps = [...prev.tap_verifications];
      newTaps[index] = { ...newTaps[index], [field]: value };
      return { ...prev, tap_verifications: newTaps };
    });
  };

  const handleAssetChange = (index, field, value) => {
    if (jobCard?.is_locked) return;
    setJobCard((prev) => {
      const newAssets = [...(prev.asset_verifications || [])];
      if (!newAssets[index]) {
        newAssets[index] = {};
      }
      newAssets[index] = { ...newAssets[index], [field]: value };
      return { ...prev, asset_verifications: newAssets };
    });
  };

  const addAssetRow = () => {
    if (jobCard?.is_locked) return;
    setJobCard((prev) => ({
      ...prev,
      asset_verifications: [...(prev.asset_verifications || []), {}],
    }));
  };

  const addTapRow = () => {
    if (jobCard?.is_locked) return;
    const currentTaps = jobCard?.tap_verifications || [];
    const nextTapNumber = currentTaps.length > 0 
      ? Math.max(...currentTaps.map(t => t.tap_number || 0)) + 1 
      : 1;
    
    setJobCard((prev) => ({
      ...prev,
      tap_verifications: [...(prev.tap_verifications || []), { tap_number: nextTapNumber, verified: false, brand: "", keg_date: "" }],
    }));
  };

  const removeTapRow = (index) => {
    if (jobCard?.is_locked) return;
    setJobCard((prev) => ({
      ...prev,
      tap_verifications: prev.tap_verifications.filter((_, i) => i !== index),
    }));
  };

  const loadInstallationAssets = async () => {
    if (jobCard?.is_locked) return;
    
    try {
      // Get customer ID from the booking
      // First try customer_id on job card, then fetch from booking
      let customerId = jobCard?.customer_id;
      
      if (!customerId && jobCard?.booking_id) {
        // Fetch booking to get customer_id
        try {
          const bookingRes = await axios.get(`${API}/service-bookings/${jobCard.booking_id}`, { withCredentials: true });
          customerId = bookingRes.data?.customer_id;
        } catch (e) {
          console.error("Error fetching booking:", e);
        }
      }
      
      if (!customerId) {
        toast.error("No customer linked to this booking");
        return;
      }
      
      // Get customer's installations
      const response = await axios.get(`${API}/customers/${customerId}/installations`, { withCredentials: true });
      const installations = response.data || [];
      
      if (installations.length === 0) {
        toast.info("No installation systems found for this customer");
        return;
      }
      
      // Convert installations to asset verification rows
      const assets = installations.map(inst => ({
        asset_id: inst.installation_id || "",
        serial_number: inst.serial_number || "",
        quantity: 1,
        notes: `${inst.system_name || ""} ${inst.model_number ? `(${inst.model_number})` : ""} - ${inst.location || ""}`.trim(),
        installation_id: inst.installation_id,
      }));
      
      setJobCard((prev) => ({
        ...prev,
        asset_verifications: assets,
      }));
      
      toast.success(`Loaded ${assets.length} installation(s) from customer profile`);
    } catch (error) {
      console.error("Error loading installation assets:", error);
      toast.error("Failed to load installation systems");
    }
  };

  const startJob = async () => {
    if (jobCard?.is_locked) return;

    try {
      setSaving(true);
      await axios.put(
        `${API}/job-cards/${bookingId}`,
        {
          job_started_at: new Date().toISOString(),
        },
        { withCredentials: true }
      );
      toast.success("Job started");
      fetchJobCard();
    } catch (error) {
      console.error("Error starting job:", error);
      toast.error("Failed to start job");
    } finally {
      setSaving(false);
    }
  };

  const saveJobCard = async () => {
    if (jobCard?.is_locked) return;
    
    try {
      setSaving(true);
      await axios.put(
        `${API}/job-cards/${bookingId}`,
        {
          sanitation_checklist: jobCard.sanitation_checklist,
          tap_verifications: jobCard.tap_verifications,
          asset_verifications: jobCard.asset_verifications,
          worker_notes: jobCard.worker_notes,
        },
        { withCredentials: true }
      );
      toast.success("Job card saved");
    } catch (error) {
      console.error("Error saving job card:", error);
      toast.error("Failed to save job card");
    } finally {
      setSaving(false);
    }
  };

  const handleWorkerSign = async () => {
    if (!workerSignature) {
      toast.error("Please draw your signature");
      return;
    }
    if (!workerSignName.trim()) {
      toast.error("Please enter your name");
      return;
    }

    try {
      setSaving(true);
      
      // Save job card first with completion time
      await axios.put(
        `${API}/job-cards/${bookingId}`,
        {
          sanitation_checklist: jobCard.sanitation_checklist,
          tap_verifications: jobCard.tap_verifications,
          asset_verifications: jobCard.asset_verifications,
          worker_notes: jobCard.worker_notes,
          job_completed_at: new Date().toISOString(),
        },
        { withCredentials: true }
      );

      // Then sign
      await axios.post(
        `${API}/job-cards/${bookingId}/worker-sign`,
        {
          signature_image: workerSignature,
          signed_by_name: workerSignName,
          confirmed: true,
        },
        { withCredentials: true }
      );
      
      toast.success("Technician signature recorded");
      setShowWorkerSignDialog(false);
      fetchJobCard();
    } catch (error) {
      console.error("Error signing job card:", error);
      toast.error(error.response?.data?.detail || "Failed to sign job card");
    } finally {
      setSaving(false);
    }
  };

  const handleCustomerSign = async () => {
    if (!customerSignature) {
      toast.error("Please draw signature");
      return;
    }
    if (!customerSignName.trim()) {
      toast.error("Please enter customer name");
      return;
    }

    try {
      setSaving(true);
      await axios.post(
        `${API}/job-cards/${bookingId}/customer-sign`,
        {
          signature_image: customerSignature,
          signed_by_name: customerSignName,
          confirmed: true,
        },
        { withCredentials: true }
      );
      
      toast.success("Customer signature recorded. Job card completed!");
      setShowCustomerSignDialog(false);
      fetchJobCard();
    } catch (error) {
      console.error("Error signing job card:", error);
      toast.error(error.response?.data?.detail || "Failed to sign job card");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const response = await axios.get(`${API}/job-cards/${bookingId}/pdf`, {
        withCredentials: true,
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `job_card_${bookingId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download PDF");
    }
  };

  const handleAssignTechnician = async (workerId) => {
    if (!workerId || workerId === "unassigned") {
      return;
    }
    try {
      await axios.post(
        `${API}/service-bookings/${bookingId}/assign-worker`,
        { worker_id: workerId },
        { withCredentials: true }
      );
      toast.success("Technician assigned successfully");
      fetchJobCard(); // Refresh to get updated technician info
    } catch (error) {
      console.error("Error assigning technician:", error);
      toast.error(error.response?.data?.detail || "Failed to assign technician");
    }
  };

  const getStatusBadge = () => {
    if (!jobCard) return null;
    const statusConfig = {
      pending: { label: "Pending", color: "bg-amber-200 text-amber-800" },
      in_progress: { label: "In Progress", color: "bg-blue-200 text-blue-800" },
      worker_signed: { label: "Technician Signed", color: "bg-indigo-200 text-indigo-800" },
      completed: { label: "Completed", color: "bg-emerald-200 text-emerald-800" },
    };
    const config = statusConfig[jobCard.status] || statusConfig.pending;
    return <Badge className={`${config.color} rounded-sm`}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48" />
          <div className="h-64 bg-slate-200 rounded" />
        </div>
      </div>
    );
  }

  if (!jobCard) {
    return (
      <div className="p-6 md:p-8">
        <Card className="rounded-sm">
          <CardContent className="p-8 text-center">
            <ClipboardCheck className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Job card not found</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isLocked = jobCard.is_locked;
  const isCustomer = currentUser?.role === "customer";
  const canEdit = !isLocked && !isCustomer && (
    currentUser?.role === "admin" ||
    currentUser?.role === "manager" ||
    (currentUser?.role === "worker" && jobCard.technician_id === currentUser?.user_id)
  );
  // Customers can only view and sign their signature block
  const isReadOnly = isLocked || isCustomer;
  // Workers must press Start Job before they can edit anything
  const workerNotStarted = currentUser?.role === "worker" && canEdit && !jobCard.job_started_at;

  return (
    <div className="p-6 md:p-8" data-testid="job-card-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
              <ClipboardCheck className="h-6 w-6" />
              Job Card
              {jobCard.job_type === "callout" && <Badge className="bg-red-100 text-red-700 hover:bg-red-100" data-testid="job-card-callout-badge">CALL-OUT</Badge>}
              {isLocked && <Lock className="h-5 w-5 text-slate-400" />}
            </h1>
            <p className="text-slate-500">Service: {jobCard.service_name || "General Service"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getStatusBadge()}
          <Button variant="outline" onClick={downloadPdf} data-testid="download-pdf-btn">
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Start Job - Prominent at top */}
      {canEdit && !jobCard.job_started_at && (
        <Card className="rounded-sm border-blue-200 bg-blue-50 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Play className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-800">Start Job</p>
                  <p className="text-sm text-blue-600">
                    {currentUser?.role === "worker"
                      ? "You must start the job before you can fill in tasks, tap verifications, or sign off."
                      : "Begin working on this service"}
                  </p>
                </div>
              </div>
              <Button
                onClick={startJob}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="start-job-btn"
              >
                <Play className="h-4 w-4 mr-2" />
                Start Job
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Job Timing - Show after job started */}
      {jobCard.job_started_at && (
        <Card className="rounded-sm border-emerald-200 bg-emerald-50 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-emerald-600" />
                <div>
                  <p className="text-xs text-slate-500">Started At</p>
                  <p className="font-medium text-emerald-700">{formatDateTime(jobCard.job_started_at)}</p>
                </div>
              </div>
              {jobCard.job_completed_at && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-emerald-600" />
                  <div>
                    <p className="text-xs text-slate-500">Completed At</p>
                    <p className="font-medium text-emerald-700">{formatDateTime(jobCard.job_completed_at)}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Venue Banner — shown when a venue is linked */}
      {jobCard.venue_name && (
        <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-sm mb-6">
          <MapPin className="h-4 w-4 text-blue-600 flex-shrink-0" />
          <div>
            <p className="text-xs text-blue-500 font-medium uppercase tracking-wide">Service Venue</p>
            <p className="text-sm font-semibold text-blue-800">{jobCard.venue_name}</p>
          </div>
        </div>
      )}

      {/* Customer & Service Info */}
      <Card className="rounded-sm border-slate-200 mb-6">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Job Card Details</CardTitle>
            <div className="text-right text-sm">
              <p className="text-slate-500">Ref: {jobCard.booking_id}</p>
              <p className="text-slate-500">Date: {jobCard.service_date}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Customer Information */}
            <div>
              <h3 className="font-semibold text-slate-900 mb-3 border-b pb-1 flex items-center gap-2">
                <User className="h-4 w-4" />
                Customer Information
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex">
                  <span className="w-24 shrink-0 text-slate-500">Name:</span>
                  <span className="flex-1 min-w-0 font-medium break-words">{jobCard.customer_name}</span>
                </div>
                <div className="flex">
                  <span className="w-24 shrink-0 text-slate-500">Phone:</span>
                  <span className="flex-1 min-w-0 break-words">{jobCard.customer_phone || "—"}</span>
                </div>
                <div className="flex">
                  <span className="w-24 shrink-0 text-slate-500">Address:</span>
                  <span className="flex-1 min-w-0 break-words">{jobCard.customer_address || "—"}</span>
                </div>
                <div className="flex">
                  <span className="w-24 shrink-0 text-slate-500">Contact:</span>
                  <span className="flex-1 min-w-0 break-words">{jobCard.contact_person || "—"}</span>
                </div>
              </div>
            </div>
            {/* Service Details */}
            <div>
              <h3 className="font-semibold text-slate-900 mb-3 border-b pb-1 flex items-center gap-2">
                <Wrench className="h-4 w-4" />
                Service Details
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex">
                  <span className="w-24 shrink-0 text-slate-500">Service:</span>
                  <span className="flex-1 min-w-0 font-medium break-words">{jobCard.service_name || "—"}</span>
                </div>
                <div className="flex">
                  <span className="w-24 shrink-0 text-slate-500">Date:</span>
                  <span className="flex-1 min-w-0 break-words">{jobCard.service_date}</span>
                </div>
                {jobCard.venue_name && (
                  <div className="flex items-center">
                    <span className="w-24 shrink-0 text-slate-500">Venue:</span>
                    <span className="flex-1 min-w-0 flex items-center gap-1 text-blue-700 font-medium break-words">
                      <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                      {jobCard.venue_name}
                    </span>
                  </div>
                )}
                <div className="flex">
                  <span className="w-24 shrink-0 text-slate-500">Installation:</span>
                  <span className="flex-1 min-w-0 break-words">{jobCard.installation_name || "—"}</span>
                </div>
                <div className="flex items-center">
                  <span className="w-24 shrink-0 text-slate-500">Technician:</span>
                  {(currentUser?.role === "admin" || currentUser?.role === "manager") && !isLocked ? (
                    <Select
                      value={jobCard.technician_id || "unassigned"}
                      onValueChange={handleAssignTechnician}
                    >
                      <SelectTrigger className="h-8 w-[180px] text-sm" data-testid="assign-technician-select">
                        <SelectValue placeholder="Assign technician" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Not assigned</SelectItem>
                        {workers.map((worker) => (
                          <SelectItem key={worker.user_id} value={worker.user_id}>
                            {worker.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="flex-1 min-w-0 break-words">{jobCard.technician_name || "Not assigned"}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sanitation Checklist */}
      <Card className="rounded-sm border-slate-200 mb-6">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Sanitation Check List</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { key: "taps_cleaned", label: "Taps Cleaned?" },
              { key: "coolers_cleaned", label: "Cooler/s Cleaned?" },
              { key: "column_lights_working", label: "Column Lights Working?" },
              { key: "columns_drip_trays_cleaned", label: "Column/s & Drip Trays Cleaned?" },
              { key: "python_cleaned", label: "Python Cleaned?" },
              { key: "regulators_set", label: "Regulator/s Set?" },
              { key: "beer_lines_sanitised", label: "Beer Line/s Sanitised?" },
              { key: "couplers_sanitised", label: "Coupler/s Sanitised?" },
              { key: "record_board_stickers", label: "Record Board Sticker/s?" },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center gap-3 p-3 border rounded-sm">
                <Checkbox
                  id={key}
                  checked={jobCard.sanitation_checklist?.[key] || false}
                  onCheckedChange={(checked) => handleChecklistChange(key, checked)}
                  disabled={isReadOnly || workerNotStarted}
                  data-testid={`checklist-${key}`}
                />
                <Label htmlFor={key} className="cursor-pointer flex-1">
                  {label}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tap Verification */}
      <Card className="rounded-sm border-slate-200 mb-6">
        <CardHeader className="pb-4 flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Tap Verification</CardTitle>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={addTapRow} disabled={workerNotStarted} data-testid="add-tap-btn">
              + Add Tap
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {jobCard.tap_verifications?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2 w-20">Tap #</th>
                    <th className="text-center p-2 w-24">Verified</th>
                    <th className="text-left p-2">Brand</th>
                    <th className="text-left p-2 w-40">Keg Date</th>
                    {canEdit && <th className="w-10"></th>}
                  </tr>
                </thead>
                <tbody>
                  {jobCard.tap_verifications?.map((tap, index) => (
                    <tr key={`tap-${tap.tap_number}`} className="border-b">
                      <td className="p-2 font-medium">{tap.tap_number}</td>
                      <td className="p-2 text-center">
                        <Checkbox
                          checked={tap.verified || false}
                          onCheckedChange={(checked) => handleTapChange(index, "verified", checked)}
                          disabled={isReadOnly || workerNotStarted}
                          data-testid={`tap-${tap.tap_number}-verified`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={tap.brand || ""}
                          onChange={(e) => handleTapChange(index, "brand", e.target.value)}
                          placeholder="Enter brand"
                          disabled={isReadOnly || workerNotStarted}
                          className="h-8"
                          data-testid={`tap-${tap.tap_number}-brand`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="date"
                          value={tap.keg_date || ""}
                          onChange={(e) => handleTapChange(index, "keg_date", e.target.value)}
                          disabled={isReadOnly || workerNotStarted}
                          className="h-8"
                          data-testid={`tap-${tap.tap_number}-keg-date`}
                        />
                      </td>
                      {canEdit && (
                        <td className="p-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-red-600"
                            onClick={() => removeTapRow(index)}
                            disabled={isReadOnly || workerNotStarted}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-6 border border-dashed rounded-sm">
              <p className="text-slate-500 mb-2">No taps added yet</p>
              {canEdit && (
                <Button variant="outline" size="sm" onClick={addTapRow} disabled={workerNotStarted}>
                  + Add First Tap
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Asset Verification */}
      <Card className="rounded-sm border-slate-200 mb-6">
        <CardHeader className="pb-4 flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Asset Verification</CardTitle>
          {canEdit && (
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={loadInstallationAssets}
                disabled={workerNotStarted}
                data-testid="load-assets-btn"
              >
                <Package className="h-4 w-4 mr-1" />
                Load from Installation
              </Button>
              <Button variant="outline" size="sm" onClick={addAssetRow} disabled={workerNotStarted}>
                + Add Asset
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {jobCard.asset_verifications?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Asset ID</th>
                    <th className="text-left p-2">Serial Number</th>
                    <th className="text-left p-2">Quantity</th>
                    <th className="text-left p-2">Notes</th>
                    {canEdit && <th className="w-10"></th>}
                  </tr>
                </thead>
                <tbody>
                  {jobCard.asset_verifications.map((asset, index) => (
                    <tr key={`asset-${asset.asset_id || asset.serial_number || index}`} className="border-b">
                      <td className="p-2">
                        <Input
                          value={asset.asset_id || ""}
                          onChange={(e) => handleAssetChange(index, "asset_id", e.target.value)}
                          placeholder="Asset ID"
                          disabled={isReadOnly || workerNotStarted}
                          className="h-8"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={asset.serial_number || ""}
                          onChange={(e) => handleAssetChange(index, "serial_number", e.target.value)}
                          placeholder="Serial #"
                          disabled={isReadOnly || workerNotStarted}
                          className="h-8"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          value={asset.quantity || ""}
                          onChange={(e) => handleAssetChange(index, "quantity", parseInt(e.target.value) || 0)}
                          placeholder="Qty"
                          disabled={isReadOnly || workerNotStarted}
                          className="h-8 w-20"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={asset.notes || ""}
                          onChange={(e) => handleAssetChange(index, "notes", e.target.value)}
                          placeholder="Notes"
                          disabled={isReadOnly || workerNotStarted}
                          className="h-8"
                        />
                      </td>
                      {canEdit && (
                        <td className="p-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-red-600"
                            onClick={() => {
                              setJobCard((prev) => ({
                                ...prev,
                                asset_verifications: prev.asset_verifications.filter((_, i) => i !== index),
                              }));
                            }}
                            disabled={isReadOnly || workerNotStarted}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-6 border border-dashed rounded-sm">
              <p className="text-slate-500 mb-2">No assets to verify</p>
              {canEdit && (
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" size="sm" onClick={loadInstallationAssets} disabled={workerNotStarted}>
                    <Package className="h-4 w-4 mr-1" />
                    Load from Installation
                  </Button>
                  <Button variant="outline" size="sm" onClick={addAssetRow} disabled={workerNotStarted}>
                    + Add Manually
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Worker Notes */}
      <Card className="rounded-sm border-slate-200 mb-6">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Technician Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={jobCard.worker_notes || ""}
            onChange={(e) => setJobCard({ ...jobCard, worker_notes: e.target.value })}
            placeholder="Add any notes about the service..."
            disabled={isReadOnly || workerNotStarted}
            rows={3}
            data-testid="worker-notes"
          />
        </CardContent>
      </Card>

      {/* Signatures */}
      <Card className="rounded-sm border-slate-200 mb-6">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Signatures</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Worker Signature */}
            <div>
              <Label className="mb-2 block font-medium">Technician Signature</Label>
              {jobCard.worker_signature?.signature_image ? (
                <div className="space-y-2">
                  <div className="border rounded-sm p-2 bg-slate-50">
                    <img
                      src={jobCard.worker_signature.signature_image}
                      alt="Technician Signature"
                      className="max-h-20"
                    />
                  </div>
                  <p className="text-sm text-slate-500">
                    Signed by: {jobCard.worker_signature.signed_by_name}
                  </p>
                  <p className="text-xs text-slate-400">
                    {formatDateTime(jobCard.worker_signature.signed_at)}
                  </p>
                </div>
              ) : canEdit && jobCard.job_started_at ? (
                <Button
                  variant="outline"
                  onClick={() => setShowWorkerSignDialog(true)}
                  className="w-full"
                  data-testid="worker-sign-btn"
                >
                  <Pen className="h-4 w-4 mr-2" />
                  Sign as Technician
                </Button>
              ) : (
                <p className="text-slate-500 text-sm">
                  {!jobCard.job_started_at ? "Start job to enable signing" : "Not signed yet"}
                </p>
              )}
            </div>

            {/* Customer Signature */}
            <div>
              <Label className="mb-2 block font-medium">Customer Signature</Label>
              {jobCard.customer_signature?.signature_image ? (
                <div className="space-y-2">
                  <div className="border rounded-sm p-2 bg-slate-50">
                    <img
                      src={jobCard.customer_signature.signature_image}
                      alt="Customer Signature"
                      className="max-h-20"
                    />
                  </div>
                  <p className="text-sm text-slate-500">
                    Signed by: {jobCard.customer_signature.signed_by_name}
                  </p>
                  <p className="text-xs text-slate-400">
                    {formatDateTime(jobCard.customer_signature.signed_at)}
                  </p>
                </div>
              ) : jobCard.worker_signature?.confirmed && !isLocked ? (
                // Allow customers OR staff to collect customer signature
                <Button
                  variant="outline"
                  onClick={() => {
                    setCustomerSignName(jobCard.contact_person || jobCard.customer_name || "");
                    setShowCustomerSignDialog(true);
                  }}
                  className="w-full"
                  data-testid="customer-sign-btn"
                >
                  <Pen className="h-4 w-4 mr-2" />
                  {isCustomer ? "Sign as Customer" : "Collect Customer Signature"}
                </Button>
              ) : (
                <p className="text-slate-500 text-sm">
                  {!jobCard.worker_signature?.confirmed
                    ? "Technician must sign first"
                    : "Not signed yet"}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {canEdit && !isLocked && !workerNotStarted && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={saveJobCard} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </div>
      )}

      {/* Worker Sign Dialog */}
      <Dialog open={showWorkerSignDialog} onOpenChange={setShowWorkerSignDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Technician Signature</DialogTitle>
            <DialogDescription>
              Sign to confirm you have completed the service. This will lock the job card from further edits.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Your Name</Label>
              <Input
                value={workerSignName}
                onChange={(e) => setWorkerSignName(e.target.value)}
                placeholder="Enter your name"
              />
            </div>
            <div>
              <Label>Draw Your Signature</Label>
              <SignatureCanvas
                onSave={(sig) => setWorkerSignature(sig)}
                existingSignature={workerSignature}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWorkerSignDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleWorkerSign} disabled={saving || !workerSignature}>
              {saving ? "Signing..." : "Confirm & Sign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Sign Dialog */}
      <Dialog open={showCustomerSignDialog} onOpenChange={setShowCustomerSignDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Customer Signature</DialogTitle>
            <DialogDescription>
              Customer signature to confirm service completion. This will finalize the job card.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Customer Name</Label>
              <Input
                value={customerSignName}
                onChange={(e) => setCustomerSignName(e.target.value)}
                placeholder="Enter customer name"
              />
            </div>
            <div>
              <Label>Customer Signature</Label>
              <SignatureCanvas
                onSave={(sig) => setCustomerSignature(sig)}
                existingSignature={customerSignature}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomerSignDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCustomerSign} disabled={saving || !customerSignature}>
              {saving ? "Signing..." : "Confirm & Complete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobCard;
